import { useState, type FormEvent } from 'react';
import { ArrowRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { NEWSLETTER_STORAGE_KEY, SUPPORT_EMAIL } from '../../../config/publicSite';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function HomeNewsletterForm({ compact = false }: Readonly<{ compact?: boolean }>) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      toast.error('Enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const existing = JSON.parse(
        localStorage.getItem(NEWSLETTER_STORAGE_KEY) ?? '[]',
      ) as string[];
      if (!existing.includes(trimmed)) {
        localStorage.setItem(
          NEWSLETTER_STORAGE_KEY,
          JSON.stringify([...existing, trimmed]),
        );
      }
      toast.success("You're on the list — we'll ping you before the next drop.");
      setEmail('');
    } catch {
      toast.error(`Something went wrong. Email us at ${SUPPORT_EMAIL}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-2' : 'space-y-3'}>
      <div
        className={
          compact
            ? 'rounded-xl border border-slate-200 bg-white p-2'
            : 'rounded-3xl border border-slate-200 bg-white/85 shadow-sm p-3'
        }
      >
        <div className={`flex flex-col gap-2 ${compact ? '' : 'sm:flex-row sm:gap-3'}`}>
          <div className="relative flex-1">
            <EnvelopeIcon
              className={`absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 ${compact ? 'h-4 w-4' : 'h-5 w-5 left-4'}`}
            />
            <input
              id="home-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={
                compact
                  ? 'w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300'
                  : 'w-full rounded-2xl border border-slate-200 bg-white py-3 pl-12 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300'
              }
              required
              aria-label="Email for drop alerts"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={
              compact
                ? 'inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-[#3B3B98] to-[#9B59B6] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:shadow-md transition-all disabled:opacity-70'
                : 'inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#3B3B98] to-[#9B59B6] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all disabled:opacity-70'
            }
          >
            {isSubmitting ? '…' : 'Get alerts'}
            {!isSubmitting ? (
              <ArrowRightIcon className={compact ? 'ml-1.5 h-3.5 w-3.5' : 'ml-2 h-4 w-4'} />
            ) : null}
          </button>
        </div>
      </div>
      {!compact ? (
        <p className="text-xs text-slate-500">
          By subscribing, you’ll receive drop alerts and offers. Unsubscribe anytime.
        </p>
      ) : null}
    </form>
  );
}
